import { css } from '@emotion/css';
import { useState } from 'react';

import {
  type DataFrame,
  type DataQueryError,
  type GrafanaTheme2,
  type QueryResultMetaNotice,
  type PanelStatusItem,
  renderMarkdown,
  textUtil,
} from '@grafana/data';
import { t } from '@grafana/i18n';
import { ClipboardButton, Icon, type IconName, Stack, TextLink, useStyles2 } from '@grafana/ui';
import { groupPanelStatusItems } from '@grafana/ui/internal';
import { PanelStatusActions } from '@grafana/ui/unstable';

export interface StandardErrorsAndNoticesInspectorProps {
  data?: DataFrame[];
  errors?: DataQueryError[];
  statusItems?: readonly PanelStatusItem[];
}

type Severity = QueryResultMetaNotice['severity'];

export interface InspectableEntry {
  severity: Severity;
  title?: string;
  content: string;
  link?: string;
  // Errors are raw payloads shown verbatim in a code block; notices are rendered as markdown.
  isCode?: boolean;
  statusItem?: PanelStatusItem;
  members?: PanelStatusItem[];
}

// Higher number = higher priority, used to sort cards error > warning > info.
const SEVERITY_RANK: Record<Severity, number> = {
  error: 3,
  warning: 2,
  info: 1,
};

const SEVERITY_LABELS: Record<Severity, () => string> = {
  error: () => t('dashboard-scene.errors-and-notices-inspector.severity-error', 'Error'),
  warning: () => t('dashboard-scene.errors-and-notices-inspector.severity-warning', 'Warning'),
  info: () => t('dashboard-scene.errors-and-notices-inspector.severity-info', 'Info'),
};

function getSeverityIcon(severity: Severity): IconName {
  return severity === 'info' ? 'info-circle' : 'exclamation-triangle';
}

function formatError(error: DataQueryError): string {
  const payload = error.data ?? error;
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return error.message ?? String(error);
  }
}

export function buildEntries(data: DataFrame[] | undefined, errors: DataQueryError[] | undefined): InspectableEntry[] {
  const entries: InspectableEntry[] = [];

  for (const error of errors ?? []) {
    entries.push({
      severity: 'error',
      title: error.message ?? error.data?.message,
      content: formatError(error),
      isCode: true,
    });
  }

  const seen = new Set<string>();
  for (const frame of data ?? []) {
    for (const notice of frame.meta?.notices ?? []) {
      const severity: Severity = notice.severity ?? 'info';
      const key = `${severity}:${notice.text}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      entries.push({
        severity,
        title: notice.text,
        content: notice.text,
        link: notice.link,
      });
    }
  }

  // Sort by severity so errors come first, then warnings, then info.
  return entries.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
}

export function StandardErrorsAndNoticesInspector({
  data,
  errors,
  statusItems,
}: StandardErrorsAndNoticesInspectorProps) {
  const styles = useStyles2(getStyles);
  const entries: InspectableEntry[] = statusItems
    ? groupPanelStatusItems(statusItems).map((members) => {
        const statusItem = members[0];
        return {
          severity: statusItem.severity,
          title: statusItem.text,
          content: statusItem.detail ?? statusItem.text,
          isCode: statusItem.origin === 'query',
          link: statusItem.link,
          statusItem,
          members,
        };
      })
    : buildEntries(data, errors);

  if (entries.length === 0) {
    return (
      <div className={styles.empty}>
        {statusItems
          ? t('dashboard-scene.errors-and-notices-inspector.no-panel-issues', 'No errors or notices for this panel.')
          : t('dashboard-scene.errors-and-notices-inspector.no-issues', 'No errors or notices for this query.')}
      </div>
    );
  }

  return (
    <Stack direction="column" gap={1}>
      {entries.map((entry, index) => (
        <EntryCard key={entry.statusItem?.id ?? `${entry.severity}-${index}`} entry={entry} />
      ))}
    </Stack>
  );
}

function EntryCard({ entry }: { entry: InspectableEntry }) {
  const styles = useStyles2(getStyles);
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-label={entry.title ?? SEVERITY_LABELS[entry.severity]()}
        >
          <Icon name={isOpen ? 'angle-up' : 'angle-down'} className={styles.chevron} />
          <Icon name={getSeverityIcon(entry.severity)} className={styles[entry.severity]} />
          <span className={styles.severityLabel}>{SEVERITY_LABELS[entry.severity]()}</span>
        </button>
        <ClipboardButton size="sm" variant="secondary" fill="text" icon="clipboard-alt" getText={() => entry.content}>
          {t('dashboard-scene.errors-and-notices-inspector.copy', 'Copy to clipboard')}
        </ClipboardButton>
      </div>

      {isOpen && (
        <div className={styles.body}>
          {entry.isCode ? (
            <>
              {entry.title && <div className={styles.message}>{entry.title}</div>}
              <pre className={styles.code}>{entry.content}</pre>
            </>
          ) : (
            // renderMarkdown sanitizes its output, so this is safe to set as HTML.
            <div className={styles.markdown} dangerouslySetInnerHTML={{ __html: renderMarkdown(entry.content) }} />
          )}
          {entry.link && (
            <TextLink href={textUtil.sanitizeUrl(entry.link)} external className={styles.link}>
              {t('dashboard-scene.errors-and-notices-inspector.learn-more', 'Learn more')}
            </TextLink>
          )}
          {entry.members?.map((member) => (
            <div key={member.id}>
              {entry.members!.length > 1 && member.refId && (
                <span>
                  {member.datasourceUid ? `${member.datasourceUid}: ` : ''}
                  {member.refId}
                </span>
              )}
              <PanelStatusActions statusItem={member} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const getStyles = (theme: GrafanaTheme2) => ({
  empty: css({
    color: theme.colors.text.secondary,
    padding: theme.spacing(2),
  }),
  card: css({
    border: `1px solid ${theme.colors.border.weak}`,
    borderRadius: theme.shape.radius.default,
    backgroundColor: theme.colors.background.secondary,
    overflow: 'hidden',
  }),
  header: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(0.5, 1, 0.5, 1.5),

    '&:hover': {
      background: theme.colors.action.hover,
    },
  }),
  toggle: css({
    display: 'flex',
    flexGrow: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
  }),
  severityLabel: css({
    fontWeight: theme.typography.fontWeightMedium,
    flexShrink: 0,
  }),
  chevron: css({
    flexShrink: 0,
    color: theme.colors.text.secondary,
  }),
  body: css({
    borderTop: `1px solid ${theme.colors.border.weak}`,
    padding: theme.spacing(1.5, 1.5, 1.5, 4.5),
  }),
  message: css({
    fontSize: theme.typography.bodySmall.fontSize,
    wordBreak: 'break-word',
    marginBottom: theme.spacing(1),
  }),
  code: css({
    margin: 0,
    padding: theme.spacing(1.5),
    fontFamily: theme.typography.fontFamilyMonospace,
    fontSize: theme.typography.bodySmall.fontSize,
    backgroundColor: theme.colors.background.canvas,
    borderRadius: theme.shape.radius.default,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
  }),
  markdown: css({
    fontSize: theme.typography.bodySmall.fontSize,
    wordBreak: 'break-word',

    // Tidy up the spacing of the rendered markdown block.
    'p:first-child': {
      marginTop: 0,
    },
    'p:last-child': {
      marginBottom: 0,
    },
    a: {
      color: theme.colors.text.link,
      textDecoration: 'underline',
    },
    code: {
      fontFamily: theme.typography.fontFamilyMonospace,
      fontSize: theme.typography.bodySmall.fontSize,
      backgroundColor: theme.colors.background.canvas,
      padding: theme.spacing(0.25, 0.5),
      borderRadius: theme.shape.radius.default,
    },
    'ul, ol': {
      paddingLeft: theme.spacing(2.5),
      margin: theme.spacing(0.5, 0),
    },
  }),
  link: css({
    display: 'inline-block',
    marginTop: theme.spacing(2),
  }),
  error: css({
    color: theme.colors.error.text,
    flexShrink: 0,
  }),
  warning: css({
    color: theme.colors.warning.text,
    flexShrink: 0,
  }),
  info: css({
    color: theme.colors.info.text,
    flexShrink: 0,
  }),
});
